import { StoreError } from "store/errors";

// A store owns its pending reads. Tab remounts join the same request instead
// of superseding a reply that is already on its way.
type ReadKey = [namespace: string, id: string, scope?: string];
type PendingRead = { promise: Promise<unknown>; expire: () => void };
const pending = new WeakMap<() => unknown, Map<string, PendingRead>>();
const cacheKeyFor = (key: ReadKey) => JSON.stringify([key[0], key[1], key[2] ?? null]);

export function invalidateResourceRead(owner: () => unknown, key: ReadKey) {
  const reads = pending.get(owner);
  const cacheKey = cacheKeyFor(key);
  reads?.get(cacheKey)?.expire();
  reads?.delete(cacheKey);
}

export function readResource<T>(
  owner: () => unknown,
  key: ReadKey,
  fetcher: (assertActive: () => void) => Promise<T>
): Promise<T> {
  let reads = pending.get(owner);
  if (!reads) {
    reads = new Map();
    pending.set(owner, reads);
  }
  const cacheKey = cacheKeyFor(key);
  const existing = reads.get(cacheKey);
  if (existing) return existing.promise as Promise<T>;

  let expired = false;
  let timer: ReturnType<typeof setTimeout>;
  const timeoutError = new StoreError(
    "This is taking longer than expected. Check your connection and retry."
  );
  const assertActive = () => { if (expired) throw timeoutError; };
  let expire!: () => void;
  const deadline = new Promise<never>((_, reject) => {
    expire = () => {
      expired = true;
      reject(timeoutError);
    };
    timer = setTimeout(() => {
      expire();
    }, 15000);
  });
  const request = Promise.race([fetcher(assertActive), deadline]).finally(() => {
    clearTimeout(timer);
    if (reads!.get(cacheKey)?.promise === request) reads!.delete(cacheKey);
  });
  reads.set(cacheKey, { promise: request, expire });
  return request;
}
